import { type Change, diffLines } from 'diff';
import { useState } from 'react';
import { JsonView, collapseAllNested, darkStyles, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import { resolveVerseEntry } from '@rb/shared/admin-verses';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useTheme } from '@/theme/theme-provider';
import { getSeededDataForComparison } from '@/client/admin.client';
import { SERVER_SEED_KEYS, type ServerSeedKey, fetchSheetData } from '@/client/seed.client';
import { useActiveVerseStore } from '@/verse/active-verse.store';

export function normalizeForSeed(key: string, value: unknown): unknown[] {
  if (value == null) return [];
  if (key === 'gameConfig') return [value];
  if (key === 'serverAttendanceRewards') return [value];
  if (key === 'serverEventAttendanceRewards') return [value];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') return Object.values(value);
  return [];
}

function JsonViewerPanel({
  title,
  jsonStr,
  count,
}: {
  title: string;
  jsonStr: string;
  count?: number;
}) {
  const { mode } = useTheme();
  let data: unknown;
  try {
    data = JSON.parse(jsonStr);
  } catch {
    data = jsonStr;
  }
  return (
    <div className="flex min-w-0 flex-1 flex-col rounded-md border bg-background">
      <div className="shrink-0 border-b px-3 py-1.5 text-xs font-medium text-muted-foreground">
        {title}
        {count !== undefined && <span className="ml-2 text-muted-foreground/70">(n={count})</span>}
      </div>
      <div className="max-h-80 overflow-auto p-3 text-xs">
        <JsonView
          data={data as object | unknown[]}
          shouldExpandNode={collapseAllNested}
          style={mode === 'dark' ? darkStyles : defaultStyles}
        />
      </div>
    </div>
  );
}

function stripUnderscoreKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUnderscoreKeys);
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as object)) {
    if (!k.startsWith('__')) result[k] = stripUnderscoreKeys(v);
  }
  return result;
}

function sortKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(sortKeys).sort((a, b) => {
      // Positional tuple (wire-format e.g. StageTuple `[id, ...]`): sort by [0] numeric id.
      if (
        Array.isArray(a) &&
        Array.isArray(b) &&
        typeof a[0] === 'number' &&
        typeof b[0] === 'number'
      ) {
        return (a[0] as number) - (b[0] as number);
      }
      const aId = (a as Record<string, unknown>)?.id;
      const bId = (b as Record<string, unknown>)?.id;
      if (typeof aId === 'number' && typeof bId === 'number') return aId - bId;
      return String(JSON.stringify(a)).localeCompare(JSON.stringify(b));
    });
  }
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(obj as object).sort()) {
    sorted[k] = sortKeys((obj as Record<string, unknown>)[k]);
  }
  return sorted;
}

type CompareResult = {
  key: ServerSeedKey;
  match: boolean;
  seededCount: number;
  jsonCount: number;
  diffChanges?: Change[];
  seededStr?: string;
  jsonStr?: string;
};

function compareCollection(
  key: ServerSeedKey,
  seeded: unknown[] | object | null | undefined,
  json: unknown
): CompareResult {
  const seededNorm = Array.isArray(seeded)
    ? seeded
    : seeded !== null && seeded !== undefined
      ? [seeded]
      : [];
  const jsonArr = Array.isArray(json) ? json : json !== null && json !== undefined ? [json] : [];
  const seededCount = Array.isArray(seeded) ? seeded.length : seeded ? 1 : 0;
  const jsonCount = Array.isArray(json) ? json.length : json ? 1 : 0;
  let seededStr = '';
  let jsonStr = '';
  try {
    seededStr = JSON.stringify(sortKeys(seededNorm), null, 2);
    jsonStr = JSON.stringify(sortKeys(jsonArr), null, 2);
    const seededNormForCompare = stripUnderscoreKeys(seededNorm);
    const normalizedSeededStr = JSON.stringify(sortKeys(seededNormForCompare), null, 2);
    const match = normalizedSeededStr === jsonStr;
    const diffChanges = match ? undefined : diffLines(normalizedSeededStr, jsonStr);
    return { key, match, seededCount, jsonCount, diffChanges, seededStr, jsonStr };
  } catch {
    return { key, match: false, seededCount, jsonCount };
  }
}

function DiffView({ changes }: { changes: Change[] }) {
  return (
    <pre className="overflow-x-auto whitespace-pre font-mono text-xs">
      {changes.map((part, i) => {
        if (part.added)
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: diff parts have no stable unique identifier
            <span key={i} className="block bg-green-500/15 text-green-500">
              {part.value}
            </span>
          );
        if (part.removed)
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: diff parts have no stable unique identifier
            <span key={i} className="block bg-destructive/15 text-destructive">
              {part.value}
            </span>
          );
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: diff parts have no stable unique identifier
          <span key={i} className="text-muted-foreground">
            {part.value}
          </span>
        );
      })}
    </pre>
  );
}

type Props = {
  env: string;
};

export function DataComparisonViewer({ env }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<CompareResult[] | null>(null);
  const [expandedKey, setExpandedKey] = useState<ServerSeedKey | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<ServerSeedKey>>(new Set(SERVER_SEED_KEYS));

  const toggleKey = (key: ServerSeedKey) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelectedKeys(new Set(SERVER_SEED_KEYS));
  const selectNone = () => setSelectedKeys(new Set());

  const handleCompare = async () => {
    const keys = Array.from(selectedKeys);
    if (keys.length === 0) {
      setError('Select at least one collection');
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      // getSeededDataForComparison 은 현재 active verse 에서 읽으므로, 비교 대상
      // CDN 파일도 같은 verse 의 flavor 로 맞춘다(리그 보상/시즌은 flavor 별 파일).
      const activeVerseId = useActiveVerseStore.getState().activeVerseId;
      if (!activeVerseId) throw new Error('[compare] no active verse — admin must connect first');
      const { flavor } = resolveVerseEntry(activeVerseId);
      const [seededData, jsonData] = await Promise.all([
        getSeededDataForComparison(keys),
        fetchSheetData(env, keys, flavor),
      ]);
      const comparison: CompareResult[] = keys.map((key) =>
        compareCollection(
          key,
          seededData[key],
          normalizeForSeed(key, (jsonData as Record<string, unknown>)[key])
        )
      );
      setResults(comparison);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-full flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">Collections</span>
          <Button type="button" size="sm" variant="ghost" onClick={selectAll}>
            All
          </Button>
          <span className="text-muted-foreground">|</span>
          <Button type="button" size="sm" variant="ghost" onClick={selectNone}>
            None
          </Button>
        </div>
        <ScrollArea className="max-h-32 rounded-md border bg-muted/40 p-3">
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {SERVER_SEED_KEYS.map((key) => (
              <Label
                key={key}
                className="flex cursor-pointer items-center gap-1.5 text-sm font-normal"
              >
                <Checkbox checked={selectedKeys.has(key)} onCheckedChange={() => toggleKey(key)} />
                <span className="font-mono text-xs">{key}</span>
              </Label>
            ))}
          </div>
        </ScrollArea>
        <Button
          type="button"
          onClick={handleCompare}
          disabled={loading || !env || selectedKeys.size === 0}
          className="w-fit"
        >
          {loading ? 'Comparing…' : `Compare (${selectedKeys.size}) with JSON (${env})`}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {results && (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-5 gap-2 px-3 text-xs font-medium text-muted-foreground">
            <span>Collection</span>
            <span>Status</span>
            <span>Seeded</span>
            <span>JSON</span>
            <span />
          </div>
          {results.map((r) => (
            <Card key={r.key} className="overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedKey(expandedKey === r.key ? null : r.key)}
                className="flex w-full items-center gap-4 p-3 text-left hover:bg-muted/40"
              >
                <span className="min-w-[180px] font-mono text-sm">{r.key}</span>
                <span
                  className={cn(
                    'min-w-[60px] font-medium',
                    r.match ? 'text-green-500' : 'text-destructive'
                  )}
                >
                  {r.match ? 'OK' : 'MISMATCH'}
                </span>
                <span className="min-w-[60px] tabular-nums">{r.seededCount}</span>
                <span className="min-w-[60px] tabular-nums">{r.jsonCount}</span>
                <span className="text-muted-foreground">{expandedKey === r.key ? '▼' : '▶'}</span>
              </button>
              {expandedKey === r.key && (r.seededStr !== undefined || r.jsonStr !== undefined) && (
                <CardContent className="flex flex-col gap-4 border-t bg-muted/20 pt-4">
                  <div className="flex gap-4 overflow-x-auto">
                    <JsonViewerPanel
                      title="Server (DB)"
                      jsonStr={r.seededStr ?? '[]'}
                      count={r.seededCount}
                    />
                    <JsonViewerPanel
                      title="JSON (Sheets)"
                      jsonStr={r.jsonStr ?? '[]'}
                      count={r.jsonCount}
                    />
                  </div>
                  {r.diffChanges && (
                    <div className="rounded-md border bg-background">
                      <div className="border-b px-3 py-1.5 text-xs font-medium text-muted-foreground">
                        Diff
                      </div>
                      <div className="max-h-64 overflow-auto p-3">
                        <p className="mb-2 text-xs text-muted-foreground">
                          <span className="text-destructive">Red</span> = in DB only{' · '}
                          <span className="text-green-500">Green</span> = in JSON only
                        </p>
                        <DiffView changes={r.diffChanges} />
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default DataComparisonViewer;
